import React, { useEffect, useState, useRef } from 'react';
import { Formik } from 'formik';
import clsx from 'clsx';
import { useSelector, useDispatch } from 'react-redux';
import {Link, useHistory, useLocation} from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { TextField, Checkbox } from '@material-ui/core';
import ReCAPTCHA from 'react-google-recaptcha';
import { actions } from '../../../store/ducks/auth/actions';
import styles from './Registration.module.scss';
import {
  passwordHelper,
  registrationCompleteText,
} from '../../../../constants';
import DialogNotifyWindow from 'app/components/DialogNotifyWindow/DialogNotifyWindow';
import { sentry } from '../../../utils/sentry';
import { AuthWrapper } from 'app/Wrappers/AuthWrapper.js';
import successIcon from 'app/assets/images/icons/successIcon.jpg';
import errorIcon from 'app/assets/images/icons/errorIcon.jpg';
import removeRedEyeIcon from 'app/assets/images/icons/remove_red_eye2.jpg';
import config from "config";
// import GoogleSignInButton from 'app/components/GoogleSignInButton/GoogleSignInButton'; // TODO: add google sign in button

const Registration = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const history = useHistory();
  const errorset = useSelector(state => state.auth.signUpError);
  const loading = useSelector(state => state.auth.loading);

  const { signUpError, disableLoading } = actions;
  const [open, setOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isExecutingCaptcha, setIsExecutingCaptcha] = useState(false);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const urlTokenParam = searchParams.get('token');
  const recaptchaRef = useRef();
  const mountedRef = useRef(true);

  useEffect(() => {
    clearErrorPanel();
    return () => {
      mountedRef.current = false;
      clearErrorPanel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
  setTimeout(() => setOpen(false), 500);
    history.push('/auth/login/');
  };

  const clearErrorPanel = () => {
    dispatch(signUpError(''));
  };

  // Safe wrapper to execute invisible reCAPTCHA with a timeout and proper reset
  async function executeRecaptcha({ timeoutMs = 20000 } = {}) {
    if (!config.recaptcha.ENABLE_GOOGLE_RECAPTCHA) return null; // guard when disabled
    if (isExecutingCaptcha) return null; // prevent concurrent executes
    if (!recaptchaRef.current) {
      throw new Error('recaptcha_not_ready');
    }
    if (mountedRef.current) setIsExecutingCaptcha(true);
    let timeoutId;
    try {
      const timeoutPromise = new Promise((_, reject) => {
        timeoutId = setTimeout(() => reject(new Error('recaptcha_timeout')), timeoutMs);
      });
      const token = await Promise.race([
        recaptchaRef.current.executeAsync(),
        timeoutPromise,
      ]);
      if (!token) throw new Error('recaptcha_no_token');
      return token;
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
      try {
        recaptchaRef.current && recaptchaRef.current.reset();
      } catch (err) {
        console.error('Error resetting reCAPTCHA:', err);
      }
      if (mountedRef.current) setIsExecutingCaptcha(false);
    }
  }

  const onRecaptchaExpired = () => {
    dispatch(signUpError('reCAPTCHA expired. Please try again.'));
    try {
      recaptchaRef.current && recaptchaRef.current.reset();
    } catch (err) {
      console.error('Error resetting reCAPTCHA on expired:', err);
    }
  };

  const onRecaptchaErrored = () => {
    dispatch(
      signUpError(
        'Could not connect to the reCAPTCHA service. Please check your internet connection and reload to get a reCAPTCHA challenge.'
      )
    );
    try {
      recaptchaRef.current && recaptchaRef.current.reset();
    } catch (err) {
      console.error('Error resetting reCAPTCHA on error:', err);
    }
  };

  async function register(values) {
    clearErrorPanel();
    const {
      email,
      password,
      lastName: family_name,
      firstName: given_name,
    } = values;

    try {
      let recaptchaToken = null;
      
      if (config.recaptcha.ENABLE_GOOGLE_RECAPTCHA) {
        try {
          recaptchaToken = await executeRecaptcha();
        } catch (e) {
          const code = e?.message || '';
          if (code === 'recaptcha_not_ready') {
            dispatch(signUpError('reCAPTCHA is not ready. Please reload the page.'));
          } else if (code === 'recaptcha_timeout') {
            dispatch(signUpError('reCAPTCHA timed out. Please try again.'));
          } else {
            dispatch(signUpError('reCAPTCHA verification failed. Please try again.'));
          }
          return false;
        }
        if (!recaptchaToken) {
          dispatch(signUpError('reCAPTCHA verification failed. Please try again.'));
          return false;
        }
      }

      // Dispatch registration action and wait for response
      return new Promise((resolve) => {
        const callback = (success) => {
          if (success) {
            handleClickOpen();
          }
          resolve(success);
        };

        dispatch(actions.signUp({
          email,
          password,
          family_name,
          given_name,
          recaptchaToken,
        }, callback));
      });
    } catch (err) {
      sentry.captureException(err, {
        username: email,
        attributes: {
          email: email,
        },
      });
      dispatch(signUpError('An error occurred during registration. Please try again.'));
      return false;
    }
  }

  return (
    <AuthWrapper>
      <div className="login-form-title">Sign up</div>
      <div className="kt-login__body">
        <div className="kt-login__form">
          <Formik
            initialValues={{
              email: urlTokenParam ? atob(urlTokenParam) : '',
              firstName: '',
              lastName: '',
              password: '',
              acceptTerms: false,
              confirmPassword: '',
            }}
            validate={values => {
              const errors = {};

              if (
                values.email &&
                !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)
              ) {
                errors.email = 'Invalid email address';
              }
              if (
                values.password &&
                !/^(?=.*[a-z])(?=.*[A-Z])(?=.*?[0-9]).{8,}$/.test(
                  values.password,
                )
              ) {
                errors.password = 'Password does not meet criteria';
              }

              if (!values.acceptTerms) {
                errors.acceptTerms = 'Accept Terms';
              }

              return errors;
            }}
            onSubmit={async (values, { setStatus, setSubmitting }) => {
              try {
                const success = await register(values);
                if (success) {
                  dispatch(disableLoading());
                } else {
                  dispatch(disableLoading());
                  setSubmitting(false);
                  setStatus(
                    intl.formatMessage({
                      id: 'AUTH.VALIDATION.INVALID_LOGIN',
                    }),
                  );
                }
              } catch (error) {
                dispatch(disableLoading());
                setSubmitting(false);
                setStatus(
                  intl.formatMessage({
                    id: 'AUTH.VALIDATION.INVALID_LOGIN',
                  }),
                );
              }
            }}
          >
            {({
              values,
              status,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
            }) => (
              <form
                onSubmit={handleSubmit}
                noValidate
                autoComplete="off"
                className="kt-form"
              >
                {config.recaptcha.ENABLE_GOOGLE_RECAPTCHA && (
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    size="invisible"
                    sitekey={config.recaptcha.SITE_KEY}
                    onErrored={onRecaptchaErrored}
                    onExpired={onRecaptchaExpired}
                  />
                )}
                {errorset ? (
                  <div role="alert" className="alert alert-danger">
                    <div className="alert-text">{errorset}</div>
                  </div>
                ) : (
                  status && (
                    <div role="alert" className="alert alert-danger">
                      <div className="alert-text">{status}</div>
                    </div>
                  )
                )}
                <div className="form-group">
                  <div className="label">First Name</div>
                  <TextField
                    margin="normal"
                    className="kt-width-full custom-form-label"
                    name="firstName"
                    autoComplete="off"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.firstName}
                    helperText={touched.firstName && errors.firstName}
                    error={Boolean(touched.firstName && errors.firstName)}
                    placeholder="First name"
                  />
                  {Boolean(touched.firstName && errors.firstName) ? (
                    <img className="error-icon" src={errorIcon} alt="icon" />
                  ) : (
                    Boolean(
                      touched.firstName &&
                        values.firstName &&
                        !errors.firstName,
                    ) && (
                      <img
                        className="success-icon"
                        src={successIcon}
                        alt="icon"
                      />
                    )
                  )}
                </div>
                <div className="form-group mb-3">
                  <div className="label">Last Name</div>
                  <TextField
                    margin="normal"
                    className="kt-width-full custom-form-label"
                    name="lastName"
                    autoComplete="off"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.lastName}
                    helperText={touched.lastName && errors.lastName}
                    error={Boolean(touched.lastName && errors.lastName)}
                    placeholder="Last name"
                  />
                  {Boolean(touched.lastName && errors.lastName) ? (
                    <img className="error-icon" src={errorIcon} alt="icon" />
                  ) : (
                    Boolean(
                      touched.lastName && values.lastName && !errors.lastName,
                    ) && (
                      <img
                        className="success-icon"
                        src={successIcon}
                        alt="icon"
                      />
                    )
                  )}
                </div>
                <div className="form-group mb-3">
                  <div className="label">Email</div>
                  <TextField
                    disabled={!!urlTokenParam}
                    autoComplete={urlTokenParam ? 'off' : 'on'}
                    margin="normal"
                    className="kt-width-full custom-form-label"
                    name="email"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.email}
                    helperText={touched.email && errors.email}
                    error={Boolean(touched.email && errors.email)}
                    placeholder="Your email"
                  />
                  {Boolean(touched.email && errors.email) ? (
                    <img className="error-icon" src={errorIcon} alt="icon" />
                  ) : (
                    Boolean(touched.email && values.email && !errors.email) && (
                      <img
                        className="success-icon"
                        src={successIcon}
                        alt="icon"
                      />
                    )
                  )}
                </div>
                <div className="form-group mb-3">
                  <div className="label">Password</div>
                  <TextField
                    type={showPassword ? 'text' : 'password'}
                    margin="normal"
                    className="kt-width-full custom-form-label"
                    name="password"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.password}
                    helperText={touched.password && errors.password}
                    error={Boolean(touched.password && errors.password)}
                    placeholder="Your password"
                  />
                  {Boolean(touched.password && errors.password) ? (
                    <img
                      className="error-icon error-icon-password"
                      src={errorIcon}
                      alt="icon"
                    />
                  ) : (
                    Boolean(
                      touched.password && values.password && !errors.password,
                    ) && (
                      <img
                        className="success-icon success-icon-password"
                        src={successIcon}
                        alt="icon"
                      />
                    )
                  )}
                  <img
                    onClick={() => setShowPassword(prev => !prev)}
                    className="hide-password-icon"
                    src={removeRedEyeIcon}
                    alt="hide"
                  />
                </div>
                <div className={styles.passwordHelper}>{passwordHelper}</div>
                <div className={styles.loginPolicy}>
                  <Checkbox
                    className={styles.loginCheckbox}
                    color="primary"
                    name="acceptTerms"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    checked={values.acceptTerms}
                  />
                  <div>
                    <span>I agree with the </span>
                    <a
                      href="https://datarova.com/privacy-policy/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      privacy policy
                    </a>
                    <span> and </span>
                    <a
                      href="https://datarova.com/terms-conditions/"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      terms of conditions,
                    </a>
                  </div>
                </div>
                <div className="login-form-actions">
                  <button
                    type="submit"
                    className={`btn button-login ${clsx({
                      'kt-spinner kt-spinner--right kt-spinner--md kt-spinner--light':
                        loading || isExecutingCaptcha || false,
                    })}`}
                    disabled={
                      !values.acceptTerms ||
                      !values.firstName ||
                      !values.lastName ||
                      !values.email ||
                      !values.password ||
                      loading ||
                      isExecutingCaptcha ||
                      errors.email ||
                      errors.password
                    }
                  >
                    Get Started
                  </button>
                 
                </div>
              </form>
            )}
          </Formik>
          {/* TODO: add google sign in button */}
          {/* <div className={styles.divider}>
            <span className={styles.dividerText}>OR</span>
          </div>

          <GoogleSignInButton 
            loading={loading} 
            buttonText="Sign up with Google" 
          /> */}

          <div className={styles.signInButton}>
            <FormattedMessage id="AUTH.GENERAL.HAVE_ACCOUNT" />
            <Link
              to="/auth"
              className="kt-link kt-login__link-register"
            >
              <FormattedMessage id="AUTH.LOGIN.TITLE" style={{}} />
            </Link>
          </div>


        </div>
        {errorset || loading ? (
          ''
        ) : (
          <DialogNotifyWindow
            text={registrationCompleteText}
            open={open}
            handleClose={handleClose}
          />
        )}
      </div>
    </AuthWrapper>
  );
};

export default Registration;
